import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import organizationsService from '../../services/crm/organizationsService';
import {
  supportService,
  SupportDomainConfig,
  SupportKbArticle,
  SupportTicketDetail,
  SupportTicketListItem,
  OrgMemberListItem,
  SupportTicketMessage,
} from '../../services/supportService';

const SupportDeskDashboard: React.FC = () => {
  const { user } = useAuth();
  const {
    activeOrgId,
    isBootstrapped,
    setActiveOrgId,
    setBootstrapped,
    setCurrentUserId
  } = useCrmOrgStore();
  
  const [active, setActive] = useState<'settings' | 'kb' | 'tickets'>('settings');

  const canLoad = useMemo(() => !!activeOrgId, [activeOrgId]);

  // Settings state
  const [domain, setDomain] = useState<SupportDomainConfig | null>(null);
  const [savingDomain, setSavingDomain] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [allowedOriginsText, setAllowedOriginsText] = useState('');

  // KB state
  const [kbLoading, setKbLoading] = useState(false);
  const [kb, setKb] = useState<SupportKbArticle[]>([]);
  const [kbEditing, setKbEditing] = useState<SupportKbArticle | null>(null);
  const [kbTitle, setKbTitle] = useState('');
  const [kbSlug, setKbSlug] = useState('');
  const [kbContent, setKbContent] = useState('');
  const [kbPublished, setKbPublished] = useState(false);
  const [kbSaving, setKbSaving] = useState(false);

  // Tickets state
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [tickets, setTickets] = useState<SupportTicketListItem[]>([]);
  const [ticketStatusFilter, setTicketStatusFilter] = useState<string>('');
  const [ticketAssignedFilter, setTicketAssignedFilter] = useState<string>('');
  const [ticketSearch, setTicketSearch] = useState<string>('');
  const [selectedTicketId, setSelectedTicketId] = useState<string>('');
  const [ticketDetailLoading, setTicketDetailLoading] = useState(false);
  const [ticketDetail, setTicketDetail] = useState<SupportTicketDetail | null>(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [members, setMembers] = useState<OrgMemberListItem[]>([]);
  const [composeText, setComposeText] = useState<string>('');
  const [composeInternal, setComposeInternal] = useState<boolean>(false);
  const [sendingMessage, setSendingMessage] = useState<boolean>(false);

  // Bootstrap organizations on mount
  useEffect(() => {
    setCurrentUserId(user?.id ?? null);
  }, [user?.id, setCurrentUserId]);

  useEffect(() => {
    const bootstrapOrganizations = async () => {
      if (!user?.id || isBootstrapped) return;

      try {
        const response = await organizationsService.list();
        const list = response.data || [];

        if (list.length === 0) {
          const created = await organizationsService.create('My Organization');
          const createdOrg = created.data;
          if (createdOrg) {
            setActiveOrgId(createdOrg.id);
          }
        } else {
          if (!activeOrgId) {
            setActiveOrgId(list[0]?.id ?? null);
          }
        }
      } catch (err: any) {
        console.error('Failed to bootstrap organizations:', err);
        toast.error(err?.message || 'Failed to load organizations');
      } finally {
        setBootstrapped(true);
      }
    };

    void bootstrapOrganizations();
  }, [user?.id, activeOrgId, isBootstrapped, setActiveOrgId, setBootstrapped]);

  useEffect(() => {
    if (!canLoad) {
      setDomain(null);
      setKb([]);
      setTickets([]);
      return;
    }

    (async () => {
      try {
        const cfg = await supportService.getDomain(activeOrgId!);
        setDomain(cfg);
        setDomainInput(cfg?.domain || '');
        setAllowedOriginsText((cfg?.allowed_origins || []).join('\n'));
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load support domain');
      }
    })();
  }, [canLoad, activeOrgId]);

  useEffect(() => {
    if (active === 'kb' && canLoad) {
      loadKb();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, canLoad]);

  useEffect(() => {
    if (active === 'tickets' && canLoad) {
      loadTickets();
      loadMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, canLoad, ticketStatusFilter, ticketAssignedFilter]);

  useEffect(() => {
    if (active !== 'tickets' || !canLoad) return;
    const t = window.setInterval(() => {
      loadTickets();
      if (selectedTicketId) loadTicketDetail(selectedTicketId);
    }, 15000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, canLoad, selectedTicketId]);

  const parseOrigins = () => {
    return allowedOriginsText
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const saveDomain = async () => {
    if (!canLoad) return;
    const dom = domainInput.trim().toLowerCase();
    if (!dom) {
      toast.error('Domain is required');
      return;
    }

    setSavingDomain(true);
    try {
      const updated = await supportService.upsertDomain({
        organization_id: activeOrgId!,
        domain: dom,
        allowed_origins: parseOrigins(),
      });
      setDomain(updated);
      toast.success('Support domain saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save support domain');
    } finally {
      setSavingDomain(false);
    }
  };

  const verifyDomain = async () => {
    if (!canLoad) return;
    setVerifyingDomain(true);
    try {
      const updated = await supportService.verifyDomain({ organization_id: activeOrgId! });
      setDomain(updated);
      toast.success('Domain verified and activated');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to verify domain');
    } finally {
      setVerifyingDomain(false);
    }
  };

  const loadKb = async () => {
    if (!canLoad) return;
    setKbLoading(true);
    try {
      const list = await supportService.listKbArticles({ organization_id: activeOrgId! });
      setKb(list);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load KB articles');
    } finally {
      setKbLoading(false);
    }
  };

  const startNewArticle = () => {
    setKbEditing(null);
    setKbTitle('');
    setKbSlug('');
    setKbContent('');
    setKbPublished(false);
  };

  const editArticle = (a: SupportKbArticle) => {
    setKbEditing(a);
    setKbTitle(a.title || '');
    setKbSlug(a.slug || '');
    setKbContent(a.content || '');
    setKbPublished(!!a.is_published);
  };

  const saveArticle = async () => {
    if (!canLoad) return;
    if (!kbTitle.trim()) return toast.error('Title is required');
    if (!kbSlug.trim()) return toast.error('Slug is required');
    if (!kbContent.trim()) return toast.error('Content is required');

    setKbSaving(true);
    try {
      if (kbEditing?.id) {
        await supportService.updateKbArticle(kbEditing.id, {
          organization_id: activeOrgId!,
          title: kbTitle.trim(),
          slug: kbSlug.trim(),
          content: kbContent,
          is_published: kbPublished,
        });
        toast.success('Article updated');
      } else {
        await supportService.createKbArticle({
          organization_id: activeOrgId!,
          title: kbTitle.trim(),
          slug: kbSlug.trim(),
          content: kbContent,
          is_published: kbPublished,
        });
        toast.success('Article created');
      }
      await loadKb();
      startNewArticle();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save article');
    } finally {
      setKbSaving(false);
    }
  };

  const deleteArticle = async (a: SupportKbArticle) => {
    if (!canLoad) return;
    if (!window.confirm(`Delete article "${a.title}"?`)) return;

    try {
      await supportService.deleteKbArticle(a.id, activeOrgId!);
      toast.success('Article deleted');
      await loadKb();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete article');
    }
  };

  const loadTickets = async () => {
    if (!canLoad) return;
    setTicketsLoading(true);
    try {
      const list = await supportService.listTickets({
        organization_id: activeOrgId!,
        status: ticketStatusFilter || undefined,
        assigned_to: ticketAssignedFilter || undefined,
        q: ticketSearch.trim() || undefined,
      });
      setTickets(list);
      if (list.length > 0 && !selectedTicketId) {
        setSelectedTicketId(list[0].id);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load tickets');
    } finally {
      setTicketsLoading(false);
    }
  };

  const loadMembers = async () => {
    if (!canLoad) return;
    setMembersLoading(true);
    try {
      const list = await supportService.listOrgMembers(activeOrgId!);
      setMembers(Array.isArray(list) ? list : []);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load organization members');
    } finally {
      setMembersLoading(false);
    }
  };

  const loadTicketDetail = async (ticketId: string) => {
    if (!canLoad) return;
    if (!ticketId) return;
    setTicketDetailLoading(true);
    try {
      const t = await supportService.getTicket(ticketId, activeOrgId!);
      setTicketDetail(t);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load ticket');
    } finally {
      setTicketDetailLoading(false);
    }
  };

  useEffect(() => {
    if (active !== 'tickets' || !canLoad) return;
    if (!selectedTicketId) {
      setTicketDetail(null);
      return;
    }
    loadTicketDetail(selectedTicketId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicketId, active, canLoad]);

  const formatMessageMeta = (m: SupportTicketMessage) => {
    const when = m.created_at ? new Date(m.created_at).toISOString().slice(0, 16).replace('T', ' ') : '';
    const visibility = m.visibility || 'public';
    const channel = m.channel || 'api';
    return `${m.sender_type} • ${visibility} • ${channel}${when ? ` • ${when}` : ''}`;
  };

  const assignTo = async (assignedTo: string | null) => {
    if (!ticketDetail?.id) return;
    try {
      const updated = await supportService.assignTicket(ticketDetail.id, {
        organization_id: activeOrgId!,
        assigned_to: assignedTo,
      });
      setTicketDetail(updated);
      await loadTickets();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to assign ticket');
    }
  };

  const sendMessage = async () => {
    if (!ticketDetail?.id) return;
    const txt = composeText.trim();
    if (!txt) return;

    setSendingMessage(true);
    try {
      if (composeInternal) {
        await supportService.addTicketNote(ticketDetail.id, { organization_id: activeOrgId!, content: txt });
      } else {
        await supportService.replyToTicket(ticketDetail.id, { organization_id: activeOrgId!, content: txt });
      }
      setComposeText('');
      await loadTicketDetail(ticketDetail.id);
      await loadTickets();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  if (!isBootstrapped) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization...</p>
        </div>
      </div>
    );
  }

  if (!activeOrgId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-12 max-w-md">
          <div className="mb-4 text-4xl">🏢</div>
          <h3 className="text-lg font-semibold text-gray-900">No Organization Selected</h3>
          <p className="mt-2 text-sm text-gray-600">
            Please create or select an organization to access the Support Desk.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Support Desk</h1>
      <p className="text-sm text-gray-600">Configure support domains, manage your knowledge base, and review tickets.</p>

      <div className="border-b">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {[
            { id: 'settings', label: 'Settings' },
            { id: 'kb', label: 'Knowledge Base' },
            { id: 'tickets', label: 'Tickets' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t.id as any)}
              className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium ${
                active === t.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {active === 'settings' && (
        <div className="bg-white border rounded p-4 space-y-4 max-w-3xl">
          <div>
            <label className="block text-sm font-medium mb-1">Support Domain (e.g. support.customer.com)</label>
            <input className="w-full border rounded px-3 py-2" value={domainInput} onChange={(e) => setDomainInput(e.target.value)} placeholder="support.customer.com" />
            <div className="text-xs text-gray-500 mt-1">Status: {domain?.status || 'not configured'}</div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Allowed Embed Origins (one per line)</label>
            <textarea className="w-full border rounded px-3 py-2" rows={5} value={allowedOriginsText} onChange={(e) => setAllowedOriginsText(e.target.value)} placeholder="https://www.customer.com\nhttps://*.customer.com" />
            <div className="text-xs text-gray-500 mt-1">Use exact origins or wildcard subdomains.</div>
          </div>

          <div className="flex gap-2">
            <button onClick={saveDomain} disabled={!canLoad || savingDomain} className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50">
              {savingDomain ? 'Saving...' : 'Save'}
            </button>
            <button onClick={verifyDomain} disabled={!canLoad || verifyingDomain} className="px-4 py-2 border rounded disabled:opacity-50">
              {verifyingDomain ? 'Verifying...' : 'Verify DNS'}
            </button>
          </div>

          {domain?.domain ? (
            <div className="text-sm text-gray-700">
              <div className="font-medium">Embed snippet</div>
              <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto">{`<script src="https://${domain.domain}/widget.js"></script>`}</pre>
              <div className="text-xs text-gray-500">This script should be placed on allowed origins only.</div>
            </div>
          ) : null}
        </div>
      )}

      {active === 'kb' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border rounded p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-medium">Articles</div>
              <button onClick={startNewArticle} className="px-3 py-1.5 border rounded text-sm">New</button>
            </div>
            {kbLoading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : (
              <div className="divide-y">
                {kb.length === 0 ? <div className="py-4 text-sm text-gray-500">No articles yet.</div> : null}
                {kb.map((a) => (
                  <div key={a.id} className="py-3 flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">{a.title}</div>
                      <div className="text-xs text-gray-500">/{a.slug} • {a.is_published ? 'published' : 'draft'}</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => editArticle(a)} className="px-3 py-1.5 border rounded text-sm">Edit</button>
                      <button onClick={() => deleteArticle(a)} className="px-3 py-1.5 border rounded text-sm">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border rounded p-4 space-y-3">
            <div className="font-medium">{kbEditing ? 'Edit Article' : 'New Article'}</div>
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input className="w-full border rounded px-3 py-2" value={kbTitle} onChange={(e) => setKbTitle(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Slug</label>
              <input className="w-full border rounded px-3 py-2" value={kbSlug} onChange={(e) => setKbSlug(e.target.value)} placeholder="how-to-reset-password" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Content</label>
              <textarea className="w-full border rounded px-3 py-2" rows={10} value={kbContent} onChange={(e) => setKbContent(e.target.value)} />
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={kbPublished} onChange={(e) => setKbPublished(e.target.checked)} />
              Published
            </label>
            <div className="flex gap-2">
              <button onClick={saveArticle} disabled={!canLoad || kbSaving} className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50">
                {kbSaving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={loadKb} disabled={!canLoad || kbLoading} className="px-4 py-2 border rounded disabled:opacity-50">Refresh</button>
            </div>
          </div>
        </div>
      )}

      {active === 'tickets' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white border rounded p-4 space-y-4 lg:col-span-1">
            <div className="flex items-center justify-between">
              <div className="font-medium">Inbox</div>
              <button onClick={loadTickets} disabled={!canLoad || ticketsLoading} className="px-3 py-1.5 border rounded text-sm disabled:opacity-50">Refresh</button>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <select className="border rounded px-3 py-2 text-sm" value={ticketStatusFilter} onChange={(e) => setTicketStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                <option value="new">New</option>
                <option value="open">Open</option>
                <option value="waiting">Waiting</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select className="border rounded px-3 py-2 text-sm" value={ticketAssignedFilter} onChange={(e) => setTicketAssignedFilter(e.target.value)}>
                <option value="">All assignees</option>
                <option value="me">Assigned to me</option>
                <option value="unassigned">Unassigned</option>
              </select>
              <input
                className="border rounded px-3 py-2 text-sm"
                value={ticketSearch}
                onChange={(e) => setTicketSearch(e.target.value)}
                placeholder="Search subject"
              />
              <button
                onClick={loadTickets}
                disabled={!canLoad || ticketsLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded text-sm disabled:opacity-50"
              >
                Search
              </button>
            </div>

            {ticketsLoading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : (
              <div className="divide-y">
                {tickets.length === 0 ? <div className="py-4 text-sm text-gray-500">No tickets.</div> : null}
                {tickets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTicketId(t.id)}
                    className={`w-full text-left py-3 ${selectedTicketId === t.id ? 'bg-indigo-50' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium">{t.subject || '(no subject)'}</div>
                        <div className="text-xs text-gray-500">
                          {t.status} • {t.priority}
                        </div>
                        <div className="text-xs text-gray-500">
                          {(t.end_user && (t.end_user.email || t.end_user.name))
                            ? `${t.end_user.name || ''} ${t.end_user.email ? `<${t.end_user.email}>` : ''}`
                            : 'unknown requester'}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">{t.created_at ? new Date(t.created_at).toISOString().slice(0, 10) : ''}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border rounded p-4 space-y-4 lg:col-span-2">
            {!selectedTicketId ? (
              <div className="text-sm text-gray-500">Select a ticket.</div>
            ) : ticketDetailLoading ? (
              <div className="text-sm text-gray-500">Loading ticket...</div>
            ) : !ticketDetail ? (
              <div className="text-sm text-gray-500">Ticket not found.</div>
            ) : (
              <>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold">{ticketDetail.subject || '(no subject)'}</div>
                    <div className="text-sm text-gray-600">{ticketDetail.end_user?.email || 'unknown requester'}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <select
                      className="border rounded px-3 py-2 text-sm"
                      value={ticketDetail.status}
                      onChange={async (e) => {
                        try {
                          const updated = await supportService.updateTicket(ticketDetail.id, {
                            organization_id: activeOrgId!,
                            status: e.target.value as any,
                          });
                          setTicketDetail(updated);
                          await loadTickets();
                        } catch (err: any) {
                          toast.error(err?.message || 'Failed to update status');
                        }
                      }}
                    >
                      <option value="new">New</option>
                      <option value="open">Open</option>
                      <option value="waiting">Waiting</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                    <select
                      className="border rounded px-3 py-2 text-sm"
                      value={ticketDetail.priority}
                      onChange={async (e) => {
                        try {
                          const updated = await supportService.updateTicket(ticketDetail.id, {
                            organization_id: activeOrgId!,
                            priority: e.target.value as any,
                          });
                          setTicketDetail(updated);
                          await loadTickets();
                        } catch (err: any) {
                          toast.error(err?.message || 'Failed to update priority');
                        }
                      }}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                    <select
                      className="border rounded px-3 py-2 text-sm"
                      value={ticketDetail.assigned_to || ''}
                      onChange={(e) => assignTo(e.target.value ? e.target.value : null)}
                      disabled={membersLoading}
                    >
                      <option value="">Unassigned</option>
                      {members.map((m) => (
                        <option key={m.id} value={m.user_id}>
                          {m.email || m.user_id}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="border rounded p-3 bg-gray-50">
                  <div className="text-sm font-medium mb-2">Timeline</div>
                  <div className="space-y-3 max-h-[420px] overflow-auto">
                    {(ticketDetail.messages || []).length === 0 ? (
                      <div className="text-sm text-gray-500">No messages yet.</div>
                    ) : (
                      (ticketDetail.messages || []).map((m) => (
                        <div key={m.id} className="bg-white border rounded p-3">
                          <div className="text-xs text-gray-500 mb-1">{formatMessageMeta(m)}</div>
                          <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="border rounded p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">Compose</div>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={composeInternal} onChange={(e) => setComposeInternal(e.target.checked)} />
                      Internal note
                    </label>
                  </div>
                  <textarea
                    className="w-full border rounded px-3 py-2"
                    rows={4}
                    value={composeText}
                    onChange={(e) => setComposeText(e.target.value)}
                    placeholder={composeInternal ? 'Write an internal note...' : 'Write a reply to the customer...'}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={sendMessage}
                      disabled={!composeText.trim() || sendingMessage}
                      className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50"
                    >
                      {sendingMessage ? 'Sending...' : 'Send'}
                    </button>
                    <button
                      onClick={() => loadTicketDetail(ticketDetail.id)}
                      disabled={ticketDetailLoading}
                      className="px-4 py-2 border rounded disabled:opacity-50"
                    >
                      Refresh
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportDeskDashboard;
